import React, {useCallback, useEffect, useRef, useState} from 'react';
import {useDispatch} from 'react-redux';
import {useNavigate} from 'react-router-dom';
import {Button, CircularProgress, Typography} from '@mui/material';
import {useTranslation} from 'react-i18next';
import {AppDispatch} from '../../../store';
import api from '../../../api/axios';
import {setAuthState, setMfaRequired, signOut} from '../../../store/slices/authSlice';
import {setAdminState} from '../../../store/slices/adminSlice';
import {getAuthStateFromLocalStorage} from '../../utils/LocalStorageAuth';
import {toast} from 'react-toastify';
import {isAxiosError} from 'axios';

const CODE_LENGTH = 6;
const ADMIN_ROLES = ['SSA', 'SA'];

const ConfirmMfa = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const inputRefs = useRef<Array<HTMLInputElement | null>>([]);

  useEffect(() => {
    // Focus first input on mount
    inputRefs.current[0]?.focus();
  }, []);

  const handleVerify = useCallback(async (codeToVerify?: string) => {
    const currentCode = codeToVerify || code;
    if (currentCode.length !== CODE_LENGTH || loading) return;

    setLoading(true);
    try {
      await api.post('/auth/mfa-verify', { totpCode: currentCode });

      // Fetch sync data explicitly after MFA verification
      const syncRes = await api.get('/sync');
      const authData = getAuthStateFromLocalStorage(syncRes.data);

      dispatch(setAuthState({
        ...authData,
        isAuthenticated: true,
        isMfaRequired: false,
      }));
      dispatch(setMfaRequired(false));

      // Fetch admin sync data if user has admin permissions
      if (authData.role && ADMIN_ROLES.includes(authData.role)) {
        try {
          const adminRes = await api.get('/admin-sync');
          dispatch(setAdminState(adminRes.data));
        } catch (adminErr) {
          console.error('Failed to fetch admin sync data:', adminErr);
        }
      }

      navigate('/market');
    } catch (err) {
      if (isAxiosError(err) && err.response?.status === 401) {
        toast.error(t('signIn.mfa.expiredOrInvalid'));
      } else {
        toast.error(t('signIn.mfa.error'));
      }
      setCode('');
      inputRefs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  }, [code, loading, dispatch, navigate, t]);

  const handleCodeChange = (value: string, index: number) => {
    if (value && !/^\d+$/.test(value)) return;

    const newCodeArr = code.split('');
    newCodeArr[index] = value.substring(value.length - 1);
    const updatedCode = newCodeArr.join('');
    setCode(updatedCode);

    if (value && index < CODE_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }

    if (updatedCode.length === CODE_LENGTH) {
      handleVerify(updatedCode);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }

    if (e.key === 'Enter') {
      handleVerify();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').slice(0, CODE_LENGTH).replace(/[^0-9]/g, '');
    setCode(pastedData);

    const nextFocusIndex = Math.min(pastedData.length, CODE_LENGTH - 1);
    inputRefs.current[nextFocusIndex]?.focus();

    if (pastedData.length === CODE_LENGTH) {
      handleVerify(pastedData);
    }
  };

  const handleLogout = () => {
    dispatch(signOut());
    navigate('/');
  };

  return (
      <div className="min-h-screen flex items-center justify-center bg-white dark:bg-neutral-800 p-4">
        <div className="max-w-md w-full bg-neutral-50 dark:bg-neutral-900 p-8 rounded-3xl shadow-xl border border-neutral-200 dark:border-neutral-800">
          <div className="text-center mb-10">
            <Typography variant="h4" className="font-black mb-3 dark:text-white">
              {t('signIn.mfa.title')}
            </Typography>
            <Typography className="text-neutral-500">
              {t('signIn.mfa.description')}
            </Typography>
          </div>

          <div className="flex justify-center gap-2 mb-10">
            {Array.from({ length: CODE_LENGTH }).map((_, i) => (
                <input
                    key={i}
                    ref={(el) => (inputRefs.current[i] = el)}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={code[i] || ''}
                    onChange={(e) => handleCodeChange(e.target.value, i)}
                    onKeyDown={(e) => handleKeyDown(e, i)}
                    onPaste={handlePaste}
                    className="w-12 h-16 text-center text-2xl font-bold rounded-xl border-2 border-neutral-300 dark:border-neutral-700 bg-transparent dark:text-white outline-none transition-all"
                />
            ))}
          </div>

          <div className="space-y-4">
            <Button
                fullWidth
                onClick={() => handleVerify()}
                disabled={code.length !== CODE_LENGTH || loading}
                variant="contained"
                className="h-14 text-white rounded-2xl font-bold text-lg normal-case"
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : t('signIn.mfa.verifyButton')}
            </Button>

            <Button
                fullWidth
                onClick={handleLogout}
                className="text-neutral-500 hover:text-neutral-700 font-medium normal-case"
            >
              {t('signIn.mfa.cancelAndLogout')}
            </Button>
          </div>
        </div>
      </div>
  );
};

export default ConfirmMfa;